import {
  amountValidation,
  isPaymentToken,
} from "../base-app/helpers/amountValidation";
import React, { useState } from "react";
import { styles } from "../styles";
import WalletModal from "../base-app/components/WalletModal";
import { BaseOperationProgress } from "../base-app/components/BaseOperationProgress";
import { BaseDepositEstimate } from "../base-app/components/BaseDepositEstimate";
import { PrivateFeeSummary } from "../base-app/components/PrivateFeeSummary";
import type { PrivateFeePolicy } from "../base-app/hooks/usePrivateFeePolicy";
import { usesPrivateFees } from "../joinsplit-v2/privateFees";
import { approvePrivateFeePolicy } from "../joinsplit-v2/privateFeeApproval";
import type { useDepositMax } from "../base-app/hooks/useDepositMax";
import { WalletAmountInput } from "../base-app/components/WalletAmountInput";
import type {
  EIP6963ProviderDetail,
  EIP1193Provider,
} from "../base-app/hooks/useEIP6963";
import type { OwnedNftOption } from "../base-app/hooks/useWalletNftDiscovery";
import { EVM_CONFIG, type TokenInfo } from "../config";

type StatusType = "success" | "error" | "info";

interface BasePaymentPageProps {
  paymentLinkError?: string | null;
  privateFeePolicy?: PrivateFeePolicy | null;
  status: { type: StatusType; message: string } | null;
  depositRecipient: string;
  recipientMpkMissing: boolean;
  recipientMpkInvalid: boolean;
  selectedToken: TokenInfo;
  setSelectedToken: (token: TokenInfo) => void;
  tokenOptions: TokenInfo[];
  useCustomNft: boolean;
  customNftAddress: string;
  setCustomNftAddress: (value: string) => void;
  customNftType: "erc721" | "erc1155";
  setCustomNftType: (value: "erc721" | "erc1155") => void;
  nftTokenId: string;
  setNftTokenId: (value: string) => void;
  ownedNftOptions: OwnedNftOption[];
  discoveringNfts: boolean;
  nftDiscoveryError: string | null;
  selectedOwnedNftIdx: string;
  setSelectedOwnedNftIdx: (value: string) => void;
  discoverWalletNfts: () => Promise<void>;
  depositAmount: string;
  setDepositAmount: (v: string) => void;
  paymentAmountLocked: boolean;
  paymentMintLocked: boolean;
  walletTokenBalance: string | null;
  loading: boolean;
  approving: boolean;
  depositProgress?: string;
  depositMax?: ReturnType<typeof useDepositMax>;
  connected: boolean;
  actionsEnabled?: boolean;
  authMessage?: string | null;
  walletAddress: string;
  handleShieldClick: () => Promise<void>;
  lastPaymentTxHash: string | null;
  getExplorerTxUrl: (txHash: string) => string | null;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
  ) => void;
  complianceStatus: "idle" | "checking" | "passed" | "blocked" | "error";
  complianceMessage: string | null;
  eip6963Providers: EIP6963ProviderDetail[];
  connectWallet: (provider: EIP1193Provider, name: string) => Promise<void>;
  disconnectWallet: () => void;
}

const BasePaymentPage: React.FC<BasePaymentPageProps> = ({
  paymentLinkError,
  privateFeePolicy,
  status,
  depositRecipient,
  recipientMpkMissing,
  recipientMpkInvalid,
  selectedToken,
  setSelectedToken,
  tokenOptions,
  useCustomNft,
  customNftType,
  depositAmount,
  setDepositAmount,
  paymentAmountLocked,
  paymentMintLocked,
  walletTokenBalance,
  loading,
  approving,
  depositProgress,
  depositMax,
  connected,
  actionsEnabled = connected,
  authMessage,
  walletAddress,
  handleShieldClick,
  lastPaymentTxHash,
  getExplorerTxUrl,
  addLog,
  complianceStatus,
  complianceMessage,
  eip6963Providers,
  connectWallet,
  disconnectWallet,
}) => {
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  const isNative = selectedToken.isNative;
  const isNftMode =
    selectedToken.tokenType === "erc721" ||
    selectedToken.tokenType === "erc1155" ||
    useCustomNft;
  const isErc721NftMode = isNftMode && customNftType === "erc721";
  const amountInputValue = isErc721NftMode ? "1" : depositAmount;
  const amountInputStep = isNftMode
    ? "1"
    : String(Math.pow(10, -Math.min(selectedToken.decimals, 6)));
  const amountInputMin = isNftMode ? "1" : "0.000001";
  const availableBalance = walletTokenBalance;
  const canUseMax =
    !paymentAmountLocked &&
    !!availableBalance &&
    actionsEnabled &&
    !loading &&
    !approving &&
    (!isNative || !!depositMax);
  const isComplianceBlocked =
    complianceStatus === "blocked" || complianceStatus === "error";
  const isComplianceChecking = complianceStatus === "checking";
  const validation = amountValidation({
    amount: depositAmount,
    decimals: selectedToken.decimals,
    symbol: selectedToken.symbol,
    balance: availableBalance,
    native: isNative,
    maxDeposit: depositMax?.quote?.amount,
    policy: privateFeePolicy,
    action: "deposit",
  });
  const validationError = isNftMode
    ? "NFT payments are not supported at this time."
    : validation.error;
  const submitDisabled =
    !!paymentLinkError ||
    (connected &&
      (!!validationError ||
        depositMax?.pending ||
        !!depositMax?.error ||
        !actionsEnabled ||
        (usesPrivateFees() && !privateFeePolicy) ||
        loading ||
        approving ||
        recipientMpkInvalid ||
        recipientMpkMissing ||
        isComplianceBlocked ||
        isComplianceChecking));

  const handleAmountChange = (value: string) => {
    if (isNftMode && value !== "" && !/^\d+$/.test(value)) return;
    setDepositAmount(value);
  };

  return (
    <div style={styles.paymentApp}>
      {walletModalOpen && (
        <WalletModal
          providers={eip6963Providers}
          onSelect={(provider, info) => {
            setWalletModalOpen(false);
            void connectWallet(provider, info.name);
          }}
          onClose={() => setWalletModalOpen(false)}
        />
      )}

      <div style={{ width: "100%", maxWidth: "440px" }}>
        {status && (
          <div
            style={{
              ...styles.paymentStatus,
              ...(status.type === "success"
                ? styles.paymentStatusSuccess
                : status.type === "error"
                  ? styles.paymentStatusError
                  : styles.paymentStatusInfo),
            }}
          >
            {status.message}
          </div>
        )}
        {!status && authMessage && connected && !actionsEnabled && (
          <div style={{ ...styles.paymentStatus, ...styles.paymentStatusInfo }}>
            {authMessage}
          </div>
        )}
        {!status &&
          !authMessage &&
          complianceMessage &&
          connected &&
          complianceStatus !== "passed" && (
            <div
              style={{
                ...styles.paymentStatus,
                ...(complianceStatus === "checking"
                  ? styles.paymentStatusInfo
                  : styles.paymentStatusError),
              }}
            >
              {complianceMessage}
            </div>
          )}

        <div style={styles.paymentCard}>
          {connected && (
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                justifyContent: "center",
                paddingBottom: "12px",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    color: "#b0c4de",
                    fontFamily: "monospace",
                  }}
                >
                  {walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}
                </span>
                <button
                  onClick={disconnectWallet}
                  style={{
                    padding: "6px 14px",
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    color: "#b0c4de",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Disconnect
                </button>
              </div>
            </div>
          )}

          <h2
            style={{
              ...styles.cardTitle,
              fontSize: "21px",
              fontWeight: 600,
              lineHeight: "24px",
              margin: "12px 0 8px",
              textAlign: "center",
            }}
          >
            Private Payment
          </h2>
          <div
            style={{
              fontSize: "13px",
              color: "#d1d5db",
              marginBottom: "28px",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            You are sending a private payment on{" "}
            {EVM_CONFIG.NETWORK?.label ?? "Base"}.
          </div>
          {paymentLinkError && <p role="alert">{paymentLinkError}</p>}
          {!paymentLinkError && depositRecipient && (
            <div style={{ marginBottom: 24, overflowWrap: "anywhere" }}>
              <div>Recipient private address</div>
              <code>{depositRecipient}</code>
            </div>
          )}

          {recipientMpkMissing && (
            <div
              style={{
                color: "#dc2626",
                marginBottom: "8px",
                fontSize: "12px",
              }}
            >
              Missing recipient address in payment link.
            </div>
          )}
          {recipientMpkInvalid && (
            <div
              style={{
                color: "#dc2626",
                marginBottom: "8px",
                fontSize: "12px",
              }}
            >
              Invalid recipient address in payment link.
            </div>
          )}

          {/* Token selector */}
          <label
            style={{
              ...styles.paymentLabel,
              color: "#E6E8EC",
              marginBottom: "8px",
              fontSize: "14px",
            }}
          >
            Token
          </label>
          {paymentMintLocked ? (
            <div
              style={{
                ...styles.paymentInput,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {selectedToken.symbol}
            </div>
          ) : (
            <select
              value={useCustomNft ? "__custom_nft__" : selectedToken.address}
              onChange={(e) => {
                const t = tokenOptions.find(
                  (tk) => tk.address === e.target.value,
                );
                if (t) setSelectedToken(t);
              }}
              style={
                {
                  ...styles.paymentInput,
                  marginBottom: "20px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  cursor: "pointer",
                  backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27%3E%3Cpath d=%27M1 1l5 5 5-5%27 stroke=%27%23b0c4de%27 fill=%27none%27/%3E%3C/svg%3E")',
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 14px center",
                  paddingRight: "36px",
                } as React.CSSProperties
              }
            >
              {tokenOptions.filter(isPaymentToken).map((t) => (
                <option
                  key={t.address}
                  value={t.address}
                  style={{ background: "#1a1a2e", color: "#e8eeff" }}
                >
                  {t.symbol} – {t.name}
                </option>
              ))}
            </select>
          )}

          {/* Amount */}
          <label
            style={{
              ...styles.paymentLabel,
              marginBottom: "8px",
              color: "#E6E8EC",
              fontSize: "14px",
            }}
          >
            {paymentAmountLocked ? "Amount due" : "Amount"} (
            {selectedToken.symbol})
          </label>
          <WalletAmountInput
            aria-label="Amount"
            step={amountInputStep}
            min={amountInputMin}
            value={amountInputValue}
            onChange={(e) => handleAmountChange(e.target.value)}
            style={{ ...styles.paymentInput, marginBottom: 0 }}
            placeholder={isNftMode ? "1" : "0.01"}
            disabled={paymentAmountLocked || isErc721NftMode}
            containerStyle={{
              marginBottom: availableBalance !== null ? "6px" : "32px",
            }}
            onMax={() => {
              if (depositMax) {
                void depositMax.selectMax();
                return;
              }
              if (!availableBalance) return;
              setDepositAmount(availableBalance);
            }}
            maxDisabled={!canUseMax || isErc721NftMode}
          />
          {availableBalance !== null && (
            <div
              style={{
                fontSize: "12px",
                color: "#b0c4de",
                marginBottom: "24px",
              }}
            >
              Available: {availableBalance} {selectedToken.symbol}
            </div>
          )}

          {connected && validationError && (
            <p role="status" style={{ color: "#ff5263", fontSize: "13px" }}>
              {validationError}
            </p>
          )}
          {usesPrivateFees() && connected && (
            <PrivateFeeSummary
              policy={privateFeePolicy ?? null}
              action="deposit"
              amount={depositAmount}
            />
          )}
          {depositMax && <BaseDepositEstimate {...depositMax} />}
          {loading && (
            <BaseOperationProgress
              message={depositProgress || "Preparing transaction…"}
            />
          )}
          <button
            style={{
              ...styles.button,
              ...styles.paymentButton,
              fontSize: "16px",
              width: "100%",
              cursor: submitDisabled ? "not-allowed" : "pointer",
              opacity: submitDisabled ? 0.5 : 1,
            }}
            onClick={() =>
              connected
                ? !submitDisabled &&
                  (approvePrivateFeePolicy(privateFeePolicy ?? null),
                  depositMax?.acceptAmount(),
                  void handleShieldClick())
                : setWalletModalOpen(true)
            }
            disabled={submitDisabled}
          >
            {!connected
              ? "Connect Wallet"
              : approving
                ? "Approving…"
                : loading
                  ? "Processing…"
                  : !actionsEnabled
                    ? "Sign In Required"
                    : `Send ${amountInputValue || ""} ${selectedToken.symbol}`}
          </button>

          {lastPaymentTxHash && (
            <div
              style={{
                marginTop: "20px",
                padding: "12px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                background: "rgba(16, 185, 129, 0.1)",
                fontSize: "13px",
              }}
            >
              <div
                style={{
                  color: "#10b981",
                  marginBottom: "6px",
                  fontWeight: 600,
                }}
              >
                Transaction Complete!
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  wordBreak: "break-all",
                  color: "#e2e8f0",
                  fontSize: "11px",
                }}
              >
                {lastPaymentTxHash}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginTop: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    background: "rgba(148, 163, 184, 0.1)",
                    color: "#e2e8f0",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                  onClick={async () => {
                    await navigator.clipboard.writeText(lastPaymentTxHash);
                    addLog("Transaction hash copied", "success");
                  }}
                >
                  Copy Tx Hash
                </button>
                {getExplorerTxUrl(lastPaymentTxHash) && (
                  <a
                    href={getExplorerTxUrl(lastPaymentTxHash) || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid rgba(56, 189, 248, 0.35)",
                      background: "rgba(56, 189, 248, 0.12)",
                      color: "#7dd3fc",
                      fontWeight: 600,
                      textDecoration: "none",
                      fontSize: "12px",
                    }}
                  >
                    View on Explorer ↗
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Powered by */}
        <div
          style={{
            textAlign: "center",
            marginTop: "20px",
            fontSize: "12px",
            color: "#4a5e82",
          }}
        >
          <img
            src="/stealthOS_logo-removebg-preview.png"
            alt="StealthOS"
            style={{ height: "40px", width: "auto", opacity: 0.5 }}
          />
        </div>
      </div>
    </div>
  );
};

export default BasePaymentPage;
